// /frontend/src/contexts/CheckoutContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { orderService } from '../services/orderService';

const CheckoutContext = createContext();

export const useCheckout = () => useContext(CheckoutContext);

export const CheckoutProvider = ({ children }) => {
  // State for checkout data
  const [shippingAddress, setShippingAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Load shipping address and payment method from localStorage
  useEffect(() => {
    const savedShippingAddress = localStorage.getItem('shippingAddress') 
      ? JSON.parse(localStorage.getItem('shippingAddress')) 
      : null;
    
    const savedPaymentMethod = localStorage.getItem('paymentMethod') || null;
    
    const savedPaymentDetails = localStorage.getItem('paymentDetails')
      ? JSON.parse(localStorage.getItem('paymentDetails'))
      : null;
    
    if (savedShippingAddress) {
      setShippingAddress(savedShippingAddress);
    }
    
    if (savedPaymentMethod) {
      setPaymentMethod(savedPaymentMethod);
    }
    
    if (savedPaymentDetails) {
      setPaymentDetails(savedPaymentDetails);
    }
  }, []);
  
  // Save shipping address
  const saveShippingAddress = (address) => {
    setShippingAddress(address);
    localStorage.setItem('shippingAddress', JSON.stringify(address));
  };
  
  // Save payment method
  const savePaymentMethod = (method) => {
    setPaymentMethod(method);
    localStorage.setItem('paymentMethod', method);
  };
  
  // Save payment details
  const savePaymentDetails = (details) => {
    // Remove sensitive information before saving
    const safeDetails = { ...details };
    if (safeDetails.cvv) delete safeDetails.cvv;
    
    setPaymentDetails(safeDetails);
    localStorage.setItem('paymentDetails', JSON.stringify(safeDetails));
  };
  
  // Create order
  const createOrder = async (orderItems, itemsPrice, taxPrice, shippingPrice, totalPrice) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const orderData = {
        orderItems,
        shippingAddress,
        paymentMethod,
        itemsPrice,
        taxPrice,
        shippingPrice,
        totalPrice
      };
      
      const createdOrder = await orderService.createOrder(orderData);
      setOrderData(createdOrder);
      return createdOrder;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Process payment
  const processPayment = async (orderId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await orderService.processPayment(orderId, paymentMethod, paymentDetails);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Clear checkout data
  const clearCheckoutData = () => {
    setShippingAddress(null);
    setPaymentMethod(null);
    setPaymentDetails(null);
    setOrderData(null);
    localStorage.removeItem('shippingAddress');
    localStorage.removeItem('paymentMethod');
    localStorage.removeItem('paymentDetails');
  };
  
  const value = {
    shippingAddress,
    paymentMethod,
    paymentDetails,
    orderData,
    isLoading,
    error,
    saveShippingAddress,
    savePaymentMethod,
    savePaymentDetails,
    createOrder,
    processPayment,
    clearCheckoutData
  };
  
  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export default CheckoutContext;